<template>
  <div class="generator-container container fade-in">
    <div class="generator-header">
      <h1>{{ $t('generator.title') }}</h1>
      <p>{{ $t('generator.subtitle') }}</p>
    </div>

    <div class="avatar-workspace">
      <div class="avatar-preview card">
        <!-- 添加动画覆盖层 -->
        <div class="sketch-animation" v-if="isGenerating">
          <svg viewBox="-100 -100 200 200" width="100%" height="100%">
            <defs>
              <filter id="sketch-filter">
                <feTurbulence type="fractalNoise" baseFrequency="0.01" numOctaves="3" result="noise" />
                <feDisplacementMap in="SourceGraphic" in2="noise" scale="5" xChannelSelector="R" yChannelSelector="G" />
                <feGaussianBlur stdDeviation="3" result="blur" />
              </filter>
            </defs>
            <g class="sketch-lines" filter="url(#sketch-filter)">
              <path v-for="(line, index) in sketchLines" :key="index" :d="line.path" fill="none" stroke="#333"
                :stroke-width="line.width"
                :style="{ animation: `drawLine ${line.duration}s ${line.delay}s forwards ease-out` }"
                stroke-dasharray="1000" stroke-dashoffset="1000" />
            </g>
          </svg>
          <div class="generating-text">{{ $t('generator.creating') }}...</div>
        </div>

        <!-- 原有SVG，添加过渡效果 -->
        <svg viewBox="-100 -100 200 200" xmlns="http://www.w3.org/2000/svg" width="500" height="500" id="face-svg"
          :class="{ 'fade-in-svg': showFadeSvg }">
          <defs>
            <clipPath id="leftEyeClipPath">
              <polyline :points="eyeLeftCountour" />
            </clipPath>
            <clipPath id="rightEyeClipPath">
              <polyline :points="eyeRightCountour" />
            </clipPath>

            <filter id="fuzzy">
              <feTurbulence id="turbulence" baseFrequency="0.05" numOctaves="3" type="noise" result="noise" />
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" />
            </filter>
            <linearGradient id="rainbowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" :style="'stop-color: ' +
                hairColors[Math.floor(Math.random() * 10)] +
                ';  stop-opacity: 1'
                " />
              <stop :offset="dyeColorOffset" :style="'stop-color: ' +
                hairColors[Math.floor(Math.random() * hairColors.length)] +
                ';  stop-opacity: 1'
                " />
              <stop offset="100%" :style="'stop-color: ' +
                hairColors[Math.floor(Math.random() * hairColors.length)] +
                ';  stop-opacity: 1'
                " />
            </linearGradient>
          </defs>
          <title>That's an ugly face</title>
          <desc>CREATED BY XUAN TANG, MORE INFO AT TXSTC55.GITHUB.IO</desc>
          <rect x="-100" y="-100" width="100%" height="100%" :fill="backgroundColors[Math.floor(Math.random() * backgroundColors.length)]
            " />
          <polyline id="faceContour" :points="computedFacePoints" fill="#ffc9a9" stroke="black"
            :stroke-width="3.0 / faceScale" stroke-linejoin="round" filter="url(#fuzzy)" />

          <g :transform="'translate(' +
            (center[0] + distanceBetweenEyes + rightEyeOffsetX) +
            ' ' +
            -(-center[1] + eyeHeightOffset + rightEyeOffsetY) +
            ')'
            ">
            <polyline id="rightCountour" :points="eyeRightCountour" fill="white" stroke="white"
              :stroke-width="0.0 / faceScale" stroke-linejoin="round" filter="url(#fuzzy)" />
          </g>
          <g :transform="'translate(' +
            -(center[0] + distanceBetweenEyes + leftEyeOffsetX) +
            ' ' +
            -(-center[1] + eyeHeightOffset + leftEyeOffsetY) +
            ')'
            ">
            <polyline id="leftCountour" :points="eyeLeftCountour" fill="white" stroke="white"
              :stroke-width="0.0 / faceScale" stroke-linejoin="round" filter="url(#fuzzy)" />
          </g>
          <g :transform="'translate(' +
            (center[0] + distanceBetweenEyes + rightEyeOffsetX) +
            ' ' +
            -(-center[1] + eyeHeightOffset + rightEyeOffsetY) +
            ')'
            ">
            <polyline id="rightUpper" :points="eyeRightUpper" fill="none" stroke="black"
              :stroke-width="(this.haventSleptForDays ? 5.0 : 3.0) / faceScale" stroke-linejoin="round"
              stroke-linecap="round" filter="url(#fuzzy)" />
            <polyline id="rightLower" :points="eyeRightLower" fill="none" stroke="black"
              :stroke-width="(this.haventSleptForDays ? 5.0 : 3.0) / faceScale" stroke-linejoin="round"
              stroke-linecap="round" filter="url(#fuzzy)" />
            <circle v-for="i in 10" :key="i" :r="Math.random() * 2 + 3.0"
              :cx="rightPupilShiftX + Math.random() * 5 - 2.5" :cy="rightPupilShiftY + Math.random() * 5 - 2.5"
              stroke="black" fill="none" :stroke-width="1.0 + Math.random() * 0.5" filter="url(#fuzzy)"
              clip-path="url(#rightEyeClipPath)" />
          </g>
          <g :transform="'translate(' +
            -(center[0] + distanceBetweenEyes + leftEyeOffsetX) +
            ' ' +
            -(-center[1] + eyeHeightOffset + leftEyeOffsetY) +
            ')'
            ">
            <polyline id="leftUpper" :points="eyeLeftUpper" fill="none" stroke="black"
              :stroke-width="(this.haventSleptForDays ? 5.0 : 3.0) / faceScale" stroke-linejoin="round"
              filter="url(#fuzzy)" />
            <polyline id="leftLower" :points="eyeLeftLower" fill="none" stroke="black"
              :stroke-width="(this.haventSleptForDays ? 5.0 : 3.0) / faceScale" stroke-linejoin="round"
              filter="url(#fuzzy)" />
            <circle v-for="i in 10" :key="i" :r="Math.random() * 2 + 3.0"
              :cx="leftPupilShiftX + Math.random() * 5 - 2.5" :cy="leftPupilShiftY + Math.random() * 5 - 2.5"
              stroke="black" fill="none" :stroke-width="1.0 + Math.random() * 0.5" filter="url(#fuzzy)"
              clip-path="url(#leftEyeClipPath)" />
          </g>
          <g id="hairs">
            <polyline v-for="(hair, index) in hairs" :key="index" :points="hair" fill="none" :stroke="hairColor"
              :stroke-width="0.5 + Math.random() * 2.5" stroke-linejoin="round" filter="url(#fuzzy)" />
          </g>
          <g id="pointNose" v-if="Math.random() > 0.5">
            <g id="rightNose">
              <circle v-for="i in 10" :key="i" :r="Math.random() * 2 + 1.0"
                :cx="rightNoseCenterX + Math.random() * 4 - 2" :cy="rightNoseCenterY + Math.random() * 4 - 2"
                stroke="black" fill="none" :stroke-width="1.0 + Math.random() * 0.5" filter="url(#fuzzy)" />
            </g>
            <g id="leftNose">
              <circle v-for="i in 10" :key="i" :r="Math.random() * 2 + 1.0"
                :cx="leftNoseCenterX + Math.random() * 4 - 2" :cy="leftNoseCenterY + Math.random() * 4 - 2"
                stroke="black" fill="none" :stroke-width="1.0 + Math.random() * 0.5" filter="url(#fuzzy)" />
            </g>
          </g>
          <g id="lineNose" v-else>
            <path :d="'M ' +
              leftNoseCenterX +
              ' ' +
              leftNoseCenterY +
              ', Q' +
              rightNoseCenterX +
              ' ' +
              rightNoseCenterY * 1.5 +
              ',' +
              (leftNoseCenterX + rightNoseCenterX) / 2 +
              ' ' +
              -eyeHeightOffset * 0.2
              " fill="none" stroke="black" :stroke-width="2.5 + Math.random() * 1.0" stroke-linejoin="round"
              filter="url(#fuzzy)"></path>
          </g>
          <g id="mouth">
            <polyline :points="mouthPoints" fill="rgb(215,127,140)" stroke="black"
              :stroke-width="2.7 + Math.random() * 0.5" stroke-linejoin="round" filter="url(#fuzzy)" />
          </g>
        </svg>
      </div>

      <div class="controls-panel">
        <div class="control-card card action-buttons">
          <h3 class="panel-title">{{ $t('generator.actions') }}</h3>
          <button @click="generateFace" class="btn btn-primary action-btn">
            <span class="btn-icon">🔄</span> {{ $t('generator.generateNew') }}
          </button>
          <button @click="downloadSVGAsPNG" class="btn btn-secondary action-btn">
            <span class="btn-icon">⬇️</span> {{ $t('generator.download') }}
          </button>
          <button @click="saveFace" class="btn btn-secondary action-btn">
            <span class="btn-icon">💾</span> {{ $t('generator.save') }}
          </button>
          <button @click="shareFace" class="btn btn-secondary action-btn">
            <span class="btn-icon">🔗</span> {{ $t('generator.share') }}
          </button>
        </div>

        <div class="control-card card control-options">
          <h3 class="panel-title">{{ $t('generator.customization') }}</h3>

          <div class="control-group">
            <label for="hairColor">{{ $t('generator.hairColor') }}</label>
            <div class="custom-select">
              <select id="hairColor" v-model="selectedHairColor" @change="updateHairColor">
                <option value="random">{{ $t('generator.random') }}</option>
                <option value="black">{{ $t('generator.black') }}</option>
                <option value="brown">{{ $t('generator.brown') }}</option>
                <option value="blonde">{{ $t('generator.blonde') }}</option>
                <option value="red">{{ $t('generator.red') }}</option>
                <option value="rainbow">{{ $t('generator.rainbow') }}</option>
              </select>
              <span class="select-arrow">▼</span>
            </div>
          </div>

          <div class="control-group">
            <label for="bgColor">{{ $t('generator.bgColor') }}</label>
            <div class="color-picker-container">
              <input type="color" id="bgColor" v-model="customBackgroundColor" @change="updateBackgroundColor"
                class="color-picker">
              <span class="color-value">{{ customBackgroundColor }}</span>
            </div>
          </div>

          <div class="control-group">
            <label for="expression">{{ $t('generator.expression') }}</label>
            <div class="custom-select">
              <select id="expression" v-model="selectedExpression" @change="generateFace">
                <option value="random">{{ $t('generator.random') }}</option>
                <option value="happy">{{ $t('generator.happy') }}</option>
                <option value="surprised">{{ $t('generator.surprised') }}</option>
                <option value="angry">{{ $t('generator.angry') }}</option>
              </select>
              <span class="select-arrow">▼</span>
            </div>
          </div>

          <div class="keyboard-shortcuts">
            <h4>{{ $t('generator.shortcuts') }}</h4>
            <div class="shortcut-item">
              <span class="key">Space</span> {{ $t('generator.space') }}
            </div>
            <div class="shortcut-item">
              <span class="key">S</span> {{ $t('generator.sKey') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast notification with animation -->
    <transition name="toast-fade">
      <div class="toast" v-if="showToast">
        <div class="toast-content">
          <span class="toast-icon">✅</span>
          <span>{{ toastMessage }}</span>
        </div>
      </div>
    </transition>

    <!-- Share dialog with improved design -->
    <transition name="dialog-fade">
      <div v-if="showShareDialog" class="share-dialog">
        <div class="share-dialog-content card">
          <button class="close-btn" @click="showShareDialog = false">×</button>
          <h3>{{ $t('generator.shareTitle') }}</h3>

          <div class="share-preview">
            <img :src="shareImageUrl" alt="avatar" />
          </div>

          <div class="share-buttons">
            <button @click="shareToTwitter" class="share-btn twitter">
              <span class="btn-icon">𝕏</span> {{ $t('generator.twitter') }}
            </button>
            <button @click="shareToFacebook" class="share-btn facebook">
              <span class="btn-icon">f</span> {{ $t('generator.facebook') }}
            </button>
            <button @click="shareNative" class="share-btn native">
              <span class="btn-icon">🔗</span> {{ $t('generator.nativeShare') }}
            </button>
            <button @click="shareDownload" class="share-btn download">
              <span class="btn-icon">⬇️</span> {{ $t('generator.downloadImage') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 添加下载选项对话框 -->
    <transition name="dialog-fade">
      <div v-if="showDownloadDialog" class="download-dialog">
        <div class="download-dialog-content card">
          <button class="close-btn" @click="showDownloadDialog = false">×</button>
          <h3>{{ $t('generator.downloadOptions') }}</h3>

          <div class="download-preview">
            <img :src="previewImageUrl" alt="Preview"
              :style="{ width: previewSize + 'px', height: previewSize + 'px' }" />
          </div>

          <div class="download-options">
            <div class="option-group">
              <label>{{ $t('generator.imageFormat') }}</label>
              <div class="radio-group">
                <label class="radio-label">
                  <input type="radio" v-model="downloadFormat" value="png" @change="updatePreview">
                  <span>PNG</span>
                </label>
                <label class="radio-label">
                  <input type="radio" v-model="downloadFormat" value="jpeg" @change="updatePreview">
                  <span>JPG</span>
                </label>
              </div>
            </div>

            <div class="option-group">
              <label>{{ $t('generator.imageSize') }}</label>
              <div class="radio-group size-options">
                <label class="radio-label" v-for="size in downloadSizes" :key="size">
                  <input type="radio" v-model="downloadSize" :value="size" @change="updatePreview">
                  <span>{{ size }}x{{ size }}</span>
                </label>
              </div>
            </div>

            <div class="option-group quality-group" v-if="downloadFormat === 'jpeg'">
              <label>{{ $t('generator.imageQuality') }}: {{ downloadQuality }}%</label>
              <input type="range" v-model="downloadQuality" min="60" max="100" step="5" @change="updatePreview">
            </div>
          </div>

          <div class="download-actions">
            <button @click="downloadWithOptions" class="btn btn-primary download-btn">
              <span class="btn-icon">⬇️</span> {{ $t('generator.download') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import * as faceShape from "../utils/face_shape.js";
import * as eyeShape from "../utils/eye_shape.js";
import * as hairLines from "../utils/hair_lines.js";
import * as mouthShape from "../utils/mouth_shape.js";
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';

function randomFromInterval(min, max) {
  // min and max included
  return Math.random() * (max - min) + min;
}

export default {
  name: "FaceGenerator",
  metaInfo() {
    return {
      title: this.t('generator.title') + ' - Ugly Avatar',
      meta: [
        { name: 'description', content: this.t('generator.subtitle') }
      ]
    };
  },
  setup() {
    const store = useStore();
    const { t } = useI18n();
    return { store, t };
  },
  data() {
    return {
      faceScale: 1.8, // face scale
      computedFacePoints: [], // the polygon points for face countour
      eyeRightUpper: [], // the points for right eye upper lid
      eyeRightLower: [],
      eyeRightCountour: [], // for the white part of the eye
      eyeLeftUpper: [],
      eyeLeftLower: [],
      eyeLeftCountour: [],
      faceHeight: 0, // the height of the face
      faceWidth: 0, // the width of the face
      center: [0, 0], // the center of the face
      distanceBetweenEyes: 0, // the distance between the eyes
      leftEyeOffsetX: 0, // the offset of the left eye
      leftEyeOffsetY: 0, // the offset of the left eye
      rightEyeOffsetX: 0, // the offset of the right eye
      rightEyeOffsetY: 0, // the offset of the right eye
      eyeHeightOffset: 0, // the offset of the eye height
      leftEyeCenter: [0, 0], // the center of the left eye
      rightEyeCenter: [0, 0], // the center of the right eye
      rightPupilShiftX: 0, // the shift of the right pupil
      rightPupilShiftY: 0, // the shift of the right pupil
      leftPupilShiftX: 0, // the shift of the left pupil
      leftPupilShiftY: 0, // the shift of the left pupil
      rightNoseCenterX: 0, // the center of the right nose
      rightNoseCenterY: 0, // the center of the right nose
      leftNoseCenterX: 0, // the center of the left nose
      leftNoseCenterY: 0, // the center of the left nose
      hairs: [],
      haventSleptForDays: false,
      hairColors: [
        "rgb(0, 0, 0)", // Black
        "rgb(44, 34, 43)", // Dark Brown
        "rgb(80, 68, 68)", // Medium Brown
        "rgb(167, 133, 106)", // Light Brown
        "rgb(220, 208, 186)", // Blond
        "rgb(233, 236, 239)", // Platinum Blond
        "rgb(165, 42, 42)", // Red
        "rgb(145, 85, 61)", // Auburn
        "rgb(128, 128, 128)", // Grey
        "rgb(185, 55, 55)", // Fire
        "rgb(255, 192, 203)", // Pastel Pink
        "rgb(255, 105, 180)", // Bright Pink
        "rgb(230, 230, 250)", // Lavender
        "rgb(64, 224, 208)", // Turquoise
        "rgb(0, 191, 255)", // Bright Blue
        "rgb(148, 0, 211)", // Deep Purple
        "rgb(50, 205, 50)", // Lime Green
        "rgb(255, 165, 0)", // Vivid Orange
        "rgb(220, 20, 60)", // Crimson Red
        "rgb(192, 192, 192)", // Silver
        "rgb(255, 215, 0)", // Gold
        "rgb(255, 255, 255)", // White
        "rgb(124, 252, 0)", // Lawn Green
        "rgb(127, 255, 0)", // Chartreuse
        "rgb(0, 255, 127)", // Spring Green
        "rgb(72, 209, 204)", // Medium Turquoise
        "rgb(0, 255, 255)", // Cyan
        "rgb(0, 206, 209)", // Dark Turquoise
        "rgb(32, 178, 170)", // Light Sea Green
        "rgb(95, 158, 160)", // Cadet Blue
        "rgb(70, 130, 180)", // Steel Blue
        "rgb(176, 196, 222)", // Light Steel Blue
        "rgb(30, 144, 255)", // Dodger Blue
        "rgb(135, 206, 235)", // Sky Blue
        "rgb(0, 0, 139)", // Dark Blue
        "rgb(138, 43, 226)", // Blue Violet
        "rgb(75, 0, 130)", // Indigo
        "rgb(139, 0, 139)", // Dark Magenta
        "rgb(153, 50, 204)", // Dark Orchid
        "rgb(186, 85, 211)", // Medium Orchid
        "rgb(218, 112, 214)", // Orchid
        "rgb(221, 160, 221)", // Plum
        "rgb(238, 130, 238)", // Violet
        "rgb(255, 0, 255)", // Magenta
        "rgb(216, 191, 216)", // Thistle
        "rgb(255, 20, 147)", // Deep Pink
        "rgb(255, 69, 0)", // Orange Red
        "rgb(255, 140, 0)", // Dark Orange
        "rgb(255, 165, 0)", // Orange
        "rgb(250, 128, 114)", // Salmon
        "rgb(233, 150, 122)", // Dark Salmon
        "rgb(240, 128, 128)", // Light Coral
        "rgb(205, 92, 92)", // Indian Red
        "rgb(255, 99, 71)", // Tomato
        "rgb(255, 160, 122)", // Light Salmon
        "rgb(220, 20, 60)", // Crimson
        "rgb(139, 0, 0)", // Dark Red
        "rgb(178, 34, 34)", // Fire Brick
        "rgb(250, 235, 215)", // Antique White
        "rgb(255, 239, 213)", // Papaya Whip
        "rgb(255, 235, 205)", // Blanched Almond
        "rgb(255, 222, 173)", // Navajo White
        "rgb(245, 245, 220)", // Beige
        "rgb(255, 228, 196)", // Bisque
        "rgb(255, 218, 185)", // Peach Puff
        "rgb(244, 164, 96)", // Sandy Brown
        "rgb(210, 180, 140)", // Tan
        "rgb(222, 184, 135)", // Burly Wood
        "rgb(250, 250, 210)", // Light Goldenrod Yellow
        "rgb(255, 250, 205)", // Lemon Chiffon
        "rgb(255, 245, 238)", // Sea Shell
        "rgb(253, 245, 230)", // Old Lace
        "rgb(255, 228, 225)", // Misty Rose
        "rgb(255, 240, 245)", // Lavender Blush
        "rgb(250, 240, 230)", // Linen
        "rgb(255, 228, 181)", // Moccasin
        "rgb(255, 250, 250)", // Snow
        "rgb(240, 255, 255)", // Azure
        "rgb(240, 255, 240)", // Honeydew
        "rgb(245, 245, 245)", // White Smoke
        "rgb(245, 255, 250)", // Mint Cream
        "rgb(240, 248, 255)", // Alice Blue
        "rgb(240, 248, 255)", // Ghost White
        "rgb(248, 248, 255)", // Ghost White
        "rgb(255, 250, 240)", // Floral White
        "rgb(253, 245, 230)", // Old Lace
      ],

      hairColor: "black",
      dyeColorOffset: "50%",
      backgroundColors: [
        "rgb(245, 245, 220)", // Soft Beige
        "rgb(176, 224, 230)", // Pale Blue
        "rgb(211, 211, 211)", // Light Grey
        "rgb(152, 251, 152)", // Pastel Green
        "rgb(255, 253, 208)", // Cream
        "rgb(230, 230, 250)", // Muted Lavender
        "rgb(188, 143, 143)", // Dusty Rose
        "rgb(135, 206, 235)", // Sky Blue
        "rgb(245, 255, 250)", // Mint Cream
        "rgb(245, 222, 179)", // Wheat
        "rgb(47, 79, 79)", // Dark Slate Gray
        "rgb(72, 61, 139)", // Dark Slate Blue
        "rgb(60, 20, 20)", // Dark Brown
        "rgb(25, 25, 112)", // Midnight Blue
        "rgb(139, 0, 0)", // Dark Red
        "rgb(85, 107, 47)", // Olive Drab
        "rgb(128, 0, 128)", // Purple
        "rgb(0, 100, 0)", // Dark Green
        "rgb(0, 0, 139)", // Dark Blue
        "rgb(105, 105, 105)", // Dim Gray
        "rgb(240, 128, 128)", // Light Coral
        "rgb(255, 160, 122)", // Light Salmon
        "rgb(255, 218, 185)", // Peach Puff
        "rgb(255, 228, 196)", // Bisque
        "rgb(255, 222, 173)", // Navajo White
        "rgb(255, 250, 205)", // Lemon Chiffon
        "rgb(250, 250, 210)", // Light Goldenrod Yellow
        "rgb(255, 239, 213)", // Papaya Whip
        "rgb(255, 245, 238)", // Sea Shell
        "rgb(255, 248, 220)", // Cornsilk
        "rgb(255, 255, 240)", // Ivory
        "rgb(240, 255, 240)", // Honeydew
        "rgb(240, 255, 255)", // Azure
        "rgb(240, 248, 255)", // Alice Blue
        "rgb(248, 248, 255)", // Ghost White
        "rgb(255, 250, 250)", // Snow
        "rgb(255, 240, 245)", // Lavender Blush
        "rgb(255, 228, 225)", // Misty Rose
        "rgb(230, 230, 250)", // Lavender
        "rgb(216, 191, 216)", // Thistle
        "rgb(221, 160, 221)", // Plum
        "rgb(238, 130, 238)", // Violet
        "rgb(218, 112, 214)", // Orchid
        "rgb(186, 85, 211)", // Medium Orchid
        "rgb(147, 112, 219)", // Medium Purple
        "rgb(138, 43, 226)", // Blue Violet
        "rgb(148, 0, 211)", // Dark Violet
        "rgb(153, 50, 204)", // Dark Orchid
        "rgb(139, 69, 19)", // Saddle Brown
        "rgb(160, 82, 45)", // Sienna
        "rgb(210, 105, 30)", // Chocolate
        "rgb(205, 133, 63)", // Peru
        "rgb(244, 164, 96)", // Sandy Brown
        "rgb(222, 184, 135)", // Burly Wood
        "rgb(255, 250, 240)", // Floral White
        "rgb(253, 245, 230)", // Old Lace
        "rgb(250, 240, 230)", // Linen
      ],
      mouthPoints: [],

      // 新增状态
      selectedHairColor: "random",
      customBackgroundColor: "#8B0000",
      selectedExpression: "random",
      showToast: false,
      toastMessage: "",
      svgData: null,
      showShareDialog: false,
      shareImageUrl: "",

      // 新增下载选项状态
      showDownloadDialog: false,
      downloadFormat: 'png',
      downloadSize: 500,
      downloadSizes: [200, 300, 500, 800, 1000],
      downloadQuality: 90,
      previewImageUrl: '',
      previewSize: 200,

      // 新增动画状态
      isGenerating: false,
      showFadeSvg: true,
      sketchLines: [],
    };
  },
  methods: {
    generateFace() {
      // 开始动画
      this.isGenerating = true;
      this.showFadeSvg = false;
      this.generateSketchLines();

      // 记录生成头像事件
      logEvent(analytics, 'generate_avatar', {
        hair_color: this.selectedHairColor,
        background_color: this.customBackgroundColor,
        expression: this.selectedExpression
      });

      // 延迟执行实际生成逻辑，等待动画完成
      setTimeout(() => {
        // 原有的头像生成代码
        this.faceScale = 1.5 + Math.random() * 0.6;
        this.haventSleptForDays = Math.random() > 0.8;
        let faceResults = faceShape.generateFaceCountourPoints();
        this.computedFacePoints = faceResults.face;
        this.faceHeight = faceResults.height;
        this.faceWidth = faceResults.width;
        this.center = faceResults.center;
        let eyes = eyeShape.generateBothEyes(this.faceWidth / 2);
        let left = eyes.left;
        let right = eyes.right;
        this.eyeRightUpper = right.upper;
        this.eyeRightLower = right.lower;
        this.eyeRightCountour = right.upper
          .slice(10, 90)
          .concat(right.lower.slice(10, 90).reverse());
        this.eyeLeftUpper = left.upper;
        this.eyeLeftLower = left.lower;
        this.eyeLeftCountour = left.upper
          .slice(10, 90)
          .concat(left.lower.slice(10, 90).reverse());
        this.distanceBetweenEyes = randomFromInterval(
          this.faceWidth / 4.5,
          this.faceWidth / 4,
        );
        this.eyeHeightOffset = randomFromInterval(
          this.faceHeight / 8,
          this.faceHeight / 6,
        );
        this.leftEyeOffsetX = randomFromInterval(
          -this.faceWidth / 20,
          this.faceWidth / 10,
        );
        this.leftEyeOffsetY = randomFromInterval(
          -this.faceHeight / 50,
          this.faceHeight / 50,
        );
        this.rightEyeOffsetX = randomFromInterval(
          -this.faceWidth / 20,
          this.faceWidth / 10,
        );
        this.rightEyeOffsetY = randomFromInterval(
          -this.faceHeight / 50,
          this.faceHeight / 50,
        );
        this.leftEyeCenter = left.center[0];
        this.rightEyeCenter = right.center[0];
        this.leftPupilShiftX = randomFromInterval(
          -this.faceWidth / 20,
          this.faceWidth / 20,
        );

        // now we generate the pupil shifts
        // we first pick a point from the upper eye lid
        let leftInd0 = Math.floor(randomFromInterval(10, left.upper.length - 10));
        let rightInd0 = Math.floor(
          randomFromInterval(10, right.upper.length - 10),
        );
        let leftInd1 = Math.floor(randomFromInterval(10, left.upper.length - 10));
        let rightInd1 = Math.floor(
          randomFromInterval(10, right.upper.length - 10),
        );
        let leftLerp = randomFromInterval(0.2, 0.8);
        let rightLerp = randomFromInterval(0.2, 0.8);

        this.leftPupilShiftY =
          left.upper[leftInd0][1] * leftLerp +
          left.lower[leftInd1][1] * (1 - leftLerp);
        this.rightPupilShiftY =
          right.upper[rightInd0][1] * rightLerp +
          right.lower[rightInd1][1] * (1 - rightLerp);
        this.leftPupilShiftX =
          left.upper[leftInd0][0] * leftLerp +
          left.lower[leftInd1][0] * (1 - leftLerp);
        this.rightPupilShiftX =
          right.upper[rightInd0][0] * rightLerp +
          right.lower[rightInd1][0] * (1 - rightLerp);

        var numHairLines = [];
        var numHairMethods = 4;
        for (var i = 0; i < numHairMethods; i++) {
          numHairLines.push(Math.floor(randomFromInterval(0, 50)));
        }
        this.hairs = [];
        if (Math.random() > 0.3) {
          this.hairs = hairLines.generateHairLines0(
            this.computedFacePoints,
            numHairLines[0] * 1 + 10,
          );
        }
        if (Math.random() > 0.3) {
          this.hairs = this.hairs.concat(
            hairLines.generateHairLines1(
              this.computedFacePoints,
              numHairLines[1] / 1.5 + 10,
            ),
          );
        }
        if (Math.random() > 0.5) {
          this.hairs = this.hairs.concat(
            hairLines.generateHairLines2(
              this.computedFacePoints,
              numHairLines[2] * 3 + 10,
            ),
          );
        }
        if (Math.random() > 0.5) {
          this.hairs = this.hairs.concat(
            hairLines.generateHairLines3(
              this.computedFacePoints,
              numHairLines[3] * 3 + 10,
            ),
          );
        }
        this.rightNoseCenterX = randomFromInterval(
          this.faceWidth / 18,
          this.faceWidth / 12,
        );
        this.rightNoseCenterY = randomFromInterval(0, this.faceHeight / 5);
        this.leftNoseCenterX = randomFromInterval(
          -this.faceWidth / 18,
          -this.faceWidth / 12,
        );
        this.leftNoseCenterY =
          this.rightNoseCenterY +
          randomFromInterval(-this.faceHeight / 30, this.faceHeight / 20);
        if (Math.random() > 0.1) {
          // use natural hair color
          this.hairColor = this.hairColors[Math.floor(Math.random() * 10)];
        } else {
          this.hairColor = "url(#rainbowGradient)";
          this.dyeColorOffset = randomFromInterval(0, 100) + "%";
        }

        var choice = Math.floor(Math.random() * 3);
        if (choice == 0) {
          this.mouthPoints = mouthShape.generateMouthShape0(
            this.computedFacePoints,
            this.faceHeight,
            this.faceWidth,
          );
        } else if (choice == 1) {
          this.mouthPoints = mouthShape.generateMouthShape1(
            this.computedFacePoints,
            this.faceHeight,
            this.faceWidth,
          );
        } else {
          this.mouthPoints = mouthShape.generateMouthShape2(
            this.computedFacePoints,
            this.faceHeight,
            this.faceWidth,
          );
        }

        // 动画结束后显示生成的头像
        setTimeout(() => {
          this.isGenerating = false;
          this.showFadeSvg = true;
        }, 400);
      }, 1200);
    },

    // 生成随机涂鸦线条
    generateSketchLines() {
      this.sketchLines = [];
      // 生成随机的勾勒线条
      const lineCount = 15 + Math.floor(Math.random() * 10);

      for (let i = 0; i < lineCount; i++) {
        const startX = Math.random() * 180 - 90;
        const startY = Math.random() * 180 - 90;

        // 创建弯曲的路径
        let path = `M ${startX} ${startY}`;

        // 添加2-4个控制点
        const pointCount = 2 + Math.floor(Math.random() * 3);
        for (let j = 0; j < pointCount; j++) {
          const cpX1 = startX + Math.random() * 60 - 30;
          const cpY1 = startY + Math.random() * 60 - 30;
          const cpX2 = startX + Math.random() * 60 - 30;
          const cpY2 = startY + Math.random() * 60 - 30;
          const x = startX + Math.random() * 80 - 40;
          const y = startY + Math.random() * 80 - 40;

          path += ` C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${x} ${y}`;
        }

        this.sketchLines.push({
          path,
          width: 0.5 + Math.random() * 2,
          duration: 0.8 + Math.random() * 0.6,
          delay: Math.random() * 0.5
        });
      }
    },

    downloadSVGAsPNG() {
      this.showDownloadDialog = true;
      this.updatePreview();
    },

    updateHairColor() {
      if (this.selectedHairColor === "random") {
        // 使用随机发色
        this.hairColor = this.hairColors[Math.floor(Math.random() * 10)];
      } else if (this.selectedHairColor === "rainbow") {
        this.hairColor = "url(#rainbowGradient)";
        this.dyeColorOffset = randomFromInterval(0, 100) + "%";
      } else {
        // 根据选择设置发色
        const colorMap = {
          black: "rgb(0, 0, 0)",
          brown: "rgb(80, 68, 68)",
          blonde: "rgb(220, 208, 186)",
          red: "rgb(165, 42, 42)"
        };
        this.hairColor = colorMap[this.selectedHairColor];
      }
    },

    updateBackgroundColor() {
      // 使用自定义背景色
      const svgElement = document.querySelector("#face-svg rect:first-child");
      if (svgElement) {
        svgElement.setAttribute("fill", this.customBackgroundColor);
      }
    },

    saveFace() {
      // 记录保存头像事件
      logEvent(analytics, 'save_avatar');

      const svg = document.getElementById("face-svg");
      const svgData = new XMLSerializer().serializeToString(svg);

      // 获取已有头像集合
      let faces = JSON.parse(localStorage.getItem('uglyAvatarCollection') || '[]');
      faces.push(svgData);
      localStorage.setItem('uglyAvatarCollection', JSON.stringify(faces));
      // 显示提示
      this.showToast = true;
      this.toastMessage = this.t('toast.saved');
      setTimeout(() => {
        this.showToast = false;
      }, 2000);
    },

    shareFace() {
      // 记录打开分享对话框事件
      logEvent(analytics, 'open_share_dialog');

      // Convert SVG to PNG DataURL first
      const svg = document.getElementById("face-svg");
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = document.createElement("img");
      const svgSize = svg.getBoundingClientRect();
      canvas.width = svgSize.width;
      canvas.height = svgSize.height;
      img.setAttribute("src", "data:image/svg+xml;base64," + btoa(svgData));
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL("image/png");
        this.shareImageUrl = dataUrl;
        this.showShareDialog = true;
      };
    },
    async uploadImageToImgbb(dataUrl) {
      // 上传图片到 imgbb，返回图片 URL
      const apiKey = process.env.IMGBB_API_KEY;
      const base64 = dataUrl.split(',')[1];
      const formData = new FormData();
      formData.append('image', base64);
      const response = await fetch(`https://api.imgbb.com/1/upload?key=${apiKey}`, {
        method: 'POST',
        body: formData
      });
      const result = await response.json();
      // 返回包含 og:image 标签的查看页面 URL
      return result.data?.display_url || '';
    },

    async shareToTwitter() {
      // 记录分享到Twitter事件
      logEvent(analytics, 'share_avatar', {
        platform: 'twitter'
      });

      const tweetText = encodeURIComponent("Check out my ugly-cute avatar! #UglyAvatar");
      const url = encodeURIComponent(window.location.href);
      let imageUrl = this.shareImageUrl;
      let imgurUrl = '';
      if (imageUrl.startsWith('data:image')) {
        imgurUrl = await this.uploadImageToImgbb(imageUrl);
      }
      // 把图片链接直接拼到文本里
      const fullText = encodeURIComponent(`${tweetText}\n${url}\n${imgurUrl || ''}`);
      window.open(
        `https://twitter.com/intent/tweet?text=${fullText}`,
        "_blank"
      );
      this.showShareDialog = false;
    },

    async shareToFacebook() {
      // 记录分享到Facebook事件
      logEvent(analytics, 'share_avatar', {
        platform: 'facebook'
      });

      let imageUrl = this.shareImageUrl;
      let pageToShare = window.location.href; // 默认分享当前页面

      if (imageUrl.startsWith('data:image')) {
        // 如果有图片，上传并获取可分享的页面链接
        const imgbbPageUrl = await this.uploadImageToImgbb(imageUrl);
        if (imgbbPageUrl) {
          pageToShare = imgbbPageUrl;
        }
      }
      // 分享包含 og:image 标签的页面
      const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageToShare)}`;
      window.open(shareUrl, "_blank");

      this.showShareDialog = false;
    },
    shareNative() {
      // 记录原生分享事件
      logEvent(analytics, 'share_avatar', {
        platform: 'native'
      });

      // Native sharing
      fetch(this.shareImageUrl)
        .then(res => res.blob())
        .then(blob => {
          if (navigator.share) {
            navigator.share({
              title: 'My Ugly-Cute Avatar',
              text: 'Check out this fun avatar I created with the Ugly-Cute Avatar Generator!',
              files: [new File([blob], 'ugly-avatar.png', { type: 'image/png' })]
            })
              .catch(error => {
                this.showToast = true;
                this.toastMessage = this.t('toast.failed');
                setTimeout(() => { this.showToast = false; }, 2000);
              });
          } else {
            this.downloadSVGAsPNG();
            this.showToast = true;
            this.toastMessage = this.t('toast.failed');
            setTimeout(() => { this.showToast = false; }, 2000);
          }
        });
      this.showShareDialog = false;
    },
    shareDownload() {
      // 下载图片
      this.downloadSVGAsPNG();
      this.showShareDialog = false;
    },

    // 更新预览图像
    updatePreview() {
      const svg = document.getElementById("face-svg");
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = document.createElement("img");

      // 设置预览图尺寸
      canvas.width = this.downloadSize;
      canvas.height = this.downloadSize;

      img.setAttribute("src", "data:image/svg+xml;base64," + btoa(svgData));
      img.onload = () => {
        ctx.drawImage(img, 0, 0, this.downloadSize, this.downloadSize);
        // 使用选择的格式生成预览
        if (this.downloadFormat === 'jpeg') {
          this.previewImageUrl = canvas.toDataURL("image/jpeg", this.downloadQuality / 100);
        } else {
          this.previewImageUrl = canvas.toDataURL("image/png");
        }
      };
    },

    // 使用选定选项下载图片
    downloadWithOptions() {
      // 记录下载头像事件
      logEvent(analytics, 'download_avatar', {
        format: this.downloadFormat,
        size: this.downloadSize,
        quality: this.downloadFormat === 'jpeg' ? this.downloadQuality : 'n/a'
      });

      const svg = document.getElementById("face-svg");
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = document.createElement("img");

      canvas.width = this.downloadSize;
      canvas.height = this.downloadSize;

      img.setAttribute("src", "data:image/svg+xml;base64," + btoa(svgData));
      img.onload = () => {
        ctx.drawImage(img, 0, 0, this.downloadSize, this.downloadSize);

        const a = document.createElement("a");
        const e = new MouseEvent("click");

        // 设置文件名和格式
        const filename = this.downloadFormat === 'jpeg' ?
          `ugly-avatar-${this.downloadSize}x${this.downloadSize}.jpg` :
          `ugly-avatar-${this.downloadSize}x${this.downloadSize}.png`;

        // 根据选择的格式生成下载链接
        if (this.downloadFormat === 'jpeg') {
          a.href = canvas.toDataURL("image/jpeg", this.downloadQuality / 100);
        } else {
          a.href = canvas.toDataURL("image/png");
        }

        a.download = filename;
        a.dispatchEvent(e);
        this.showDownloadDialog = false;
      };
    },
  },
  mounted() {
    this.generateFace();
    // add key binding
    window.addEventListener("keydown", (e) => {
      if (e.key === " ") {
        this.generateFace();
        // this.downloadSVGAsPNG();
      } else if (e.key === "s") {
        this.downloadSVGAsPNG();
      }
    });
    
    // 记录页面访问事件
    logEvent(analytics, 'screen_view', {
      screen_name: 'generator'
    });
  }
};
</script>

<style scoped>
.generator-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  overflow: hidden;
}

.generator-header {
  text-align: center;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
}

.generator-header h1 {
  font-size: 2rem;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;
}

.generator-header p {
  color: var(--text-secondary);
  font-size: 1rem;
  margin: 0;
}

.avatar-workspace {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 1.5rem;
  align-items: flex-start;
  flex: 1;
  min-height: 0;
}

.avatar-preview {
  width: 100%;
  padding: 1rem;
  background-color: white;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  /* 添加相对定位 */
}

.avatar-preview svg {
  width: 100%;
  max-width: 500px;
  height: auto;
  display: block;
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.1));
}

.controls-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  overflow-y: auto;
}

.control-card {
  padding: 1rem;
  flex-shrink: 0;
}

.panel-title {
  margin-bottom: 1rem;
  color: var(--secondary-color);
  font-size: 1.1rem;
  font-weight: 600;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  font-weight: 500;
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
}

.action-btn:hover {
  color: var(--text-light);
}

.btn-secondary.action-btn:hover {
  color: var(--text-primary);
}

.control-group {
  margin-bottom: 1rem;
}

.control-group label {
  display: block;
  margin-bottom: 0.4rem;
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 0.9rem;
}

.custom-select {
  position: relative;
}

.custom-select select {
  width: 100%;
  padding: 0.5rem 0.8rem;
  border: 1px solid #ddd;
  border-radius: var(--border-radius-md);
  appearance: none;
  background-color: white;
  font-size: 0.9rem;
  cursor: pointer;
}

.select-arrow {
  position: absolute;
  right: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  pointer-events: none;
  font-size: 0.7rem;
}

.color-picker-container {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.color-picker {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  padding: 0;
}

.color-value {
  font-family: monospace;
  background-color: #f5f5f5;
  padding: 0.3rem 0.6rem;
  border-radius: var(--border-radius-sm);
  color: var(--text-secondary);
  font-size: 0.8rem;
}

.keyboard-shortcuts {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.keyboard-shortcuts h4 {
  font-size: 0.9rem;
  margin-bottom: 0.8rem;
  color: var(--secondary-color);
}

.shortcut-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.8rem;
}

.key {
  display: inline-block;
  padding: 0.2rem 0.4rem;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: var(--border-radius-sm);
  margin-right: 0.5rem;
  font-family: monospace;
  font-weight: 600;
  min-width: 1.5rem;
  text-align: center;
  font-size: 0.7rem;
}

.toast {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: var(--secondary-color);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.toast-icon {
  font-size: 1.1rem;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.3s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 20px);
}

.share-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.share-dialog-content {
  width: 100%;
  max-width: 500px;
  padding: 2rem;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #f0f0f0;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  cursor: pointer;
  border: none;
  padding: 0;
}

.share-dialog h3 {
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  color: var(--secondary-color);
}

.share-preview {
  width: 200px;
  height: 200px;
  margin: 0 auto 1.5rem;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  border: 1px solid #eee;
}

.share-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.share-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.share-btn {
  padding: 0.75rem 0;
  border-radius: var(--border-radius-md);
  border: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: var(--transition);
}

.share-btn:hover {
  opacity: 0.9;
  color: var(--text-light);
  /* 确保分享按钮悬停时文字可见 */
}

/* 下载按钮悬停样式特殊处理 */
.share-btn.download:hover {
  color: var(--text-primary);
}

.share-btn.twitter {
  background-color: #1da1f2;
  color: white;
}

.share-btn.facebook {
  background-color: #4267b2;
  color: white;
}

.share-btn.native {
  background-color: var(--primary-color);
  color: white;
  grid-column: span 2;
}

.share-btn.download {
  background-color: #f5f5f5;
  color: var(--text-primary);
  grid-column: span 2;
}

.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: all 0.3s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
}

.dialog-fade-enter-from .share-dialog-content,
.dialog-fade-leave-to .share-dialog-content {
  transform: scale(0.9);
}

.download-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.download-dialog-content {
  width: 100%;
  max-width: 500px;
  padding: 2rem;
  position: relative;
}

.download-preview {
  width: 200px;
  height: 200px;
  margin: 0 auto 1.5rem;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  border: 1px solid #eee;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.download-preview img {
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
}

.download-options {
  margin-bottom: 1.5rem;
}

.option-group {
  margin-bottom: 1rem;
}

.option-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.radio-group {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.size-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 0.5rem;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.4rem 0.8rem;
  background: #f5f5f5;
  border-radius: var(--border-radius-md);
  transition: all 0.2s ease;
}

.radio-label:hover {
  background: #eeeeee;
}

.radio-label input {
  margin-right: 0.5rem;
}

.quality-group input[type="range"] {
  width: 100%;
  margin-top: 0.5rem;
}

.download-actions {
  display: flex;
  justify-content: center;
}

.download-btn {
  min-width: 150px;
}

.sketch-animation {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 5;
}

.generating-text {
  position: absolute;
  bottom: 20px;
  font-size: 1.2rem;
  color: var(--secondary-color);
  font-weight: 500;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.6;
  }
}

@keyframes drawLine {
  to {
    stroke-dashoffset: 0;
  }
}

.fade-in-svg {
  animation: fadeInSvg 0.6s ease-in-out;
}

@keyframes fadeInSvg {
  from {
    opacity: 0;
    filter: blur(10px);
  }

  to {
    opacity: 1;
    filter: blur(0);
  }
}

@media (max-width: 500px) {
  .download-dialog-content {
    padding: 1.5rem;
  }

  .download-preview {
    width: 150px;
    height: 150px;
  }

  .radio-group {
    flex-direction: column;
    gap: 0.5rem;
  }

  .size-options {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 992px) {
  .generator-container {
    padding: 0.5rem;
  }

  .avatar-workspace {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
    gap: 1rem;
  }

  .generator-header h1 {
    font-size: 1.8rem;
  }

  .controls-panel {
    max-height: 40vh;
    overflow-y: auto;
  }

  .action-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }

  .action-btn {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
}

@media (max-width: 768px) {
  .generator-header {
    margin-bottom: 1rem;
  }

  .generator-header h1 {
    font-size: 1.5rem;
  }

  .generator-header p {
    font-size: 0.9rem;
  }

  .controls-panel {
    max-height: 35vh;
  }

  .control-card {
    padding: 0.8rem;
  }

  .panel-title {
    font-size: 1rem;
  }
}

@media (max-width: 500px) {
  .generator-container {
    padding: 0.25rem;
  }

  .avatar-workspace {
    gap: 0.8rem;
  }

  .action-buttons {
    grid-template-columns: 1fr;
  }

  .share-buttons {
    grid-template-columns: 1fr;
  }

  .share-btn.native,
  .share-btn.download {
    grid-column: auto;
  }

  .controls-panel {
    max-height: 30vh;
  }
}

@media (max-height: 600px) {
  .generator-header {
    margin-bottom: 0.5rem;
  }

  .generator-header h1 {
    font-size: 1.3rem;
    margin-bottom: 0.25rem;
  }

  .generator-header p {
    font-size: 0.8rem;
  }

  .controls-panel {
    max-height: 45vh;
  }

  .control-card {
    padding: 0.6rem;
  }

  .keyboard-shortcuts {
    display: none;
  }
}
</style>
